import { PassedSundaysWrittenDownMark } from './api_types.ts';
import {
  addDayOffset,
  DateString,
  DayOfWeek,
  getAllEnclosingSundays,
  getDayOfWeek,
  getFirstDayOfMonth,
  thisSundayDate,
} from './date_string.ts';
import {
  EventSongList,
  Org,
  OrgType,
  RepeatKind,
  SongList,
  SongListEntry,
  SongListSchemaEntry,
  SongListType,
} from './model.ts';
import { blankEntry, getSundayOrdinal, isBlankEntry, SongListChanges } from './model_util.ts';
import { getFillStartSunday } from './passed_sunday_clock.ts';
import { getRepeatFrom, getRepeatKind, getRowEntries, resolveRepeatingCell, RowEntries } from './repeating_rows.ts';
import { ensureUnreachable } from './util.ts';

// A church's passed Sundays are written down (issue #4192). From the current Sunday on, a repeating row fills a Sunday
// that holds nothing from an earlier Sunday (repeating_rows.ts), so a Sunday that only showed a filled-in song would go
// blank once it passed, or once the repeat was turned off. Once a Sunday has passed, what each repeating row showed on
// it is written into its weekly list, so a church's history no longer depends on a setting. Michael: "history is
// history and it shouldn't be changed." A blank never stays in the past: a passed Sunday's blank is removed at every
// row and, at a repeating row whose next Sunday in scope holds nothing, moves there, so the block goes on interrupting
// the repeat. A By-Sunday 5th Sunday that borrows its month's 1st Sunday (repeating_rows.ts) needs no blank of its own:
// a 1st Sunday written down holding nothing shows nothing, and so does its 5th Sunday. The planner writes the same down
// itself before a change to a row (getWriteDownBeforeRowChange), and before any edit of a church's lists
// (getPassedSundayRangeWrites, client/src/pages/admin/org/planner_edits.ts, issue #4209), for every passed Sunday the
// server has not written yet. Until a passed Sunday is written down, every reader goes on filling it on screen
// (getFillStartSunday in passed_sunday_clock.ts, which reads how far the server has written each church, GET
// /api/passed-sundays), so nothing goes blank between the Sunday passing and the write, however late the write. Schools
// are never touched. Only Sunday worship services take part, as in RowEntries: an old weekday service (worship services
// are Sundays only since 2025-03-01, see validateSongList on the server) is left exactly as stored, its `text: ''`
// entries included.

// The next Sunday after `sunday` in the kind's scope, where a passed Sunday's blank moves to: the next Sunday for Same
// every Sunday; the next Sunday when it is in the same month for Same all month (a month's fill reads only its own
// Sundays, so a blank on its last Sunday blocks nothing); the next Sunday of the same ordinal for By Sunday of the month
// (a 5th Sunday's blank moves to the next 5th Sunday, which it blocked before too, since a 5th Sunday fills from the
// nearest earlier 5th Sunday); undefined for Does not repeat.
export function getNextSundayInScope(kind: RepeatKind, sunday: DateString): DateString | undefined {
  const nextSunday = addDayOffset(sunday, 7);
  switch (kind) {
    case RepeatKind.DoesNotRepeat:
      return undefined;
    case RepeatKind.SameEverySunday:
      return nextSunday;
    case RepeatKind.SameAllMonth:
      return getFirstDayOfMonth(nextSunday) === getFirstDayOfMonth(sunday) ? nextSunday : undefined;
    case RepeatKind.BySundayOfMonth: {
      const ordinal = getSundayOrdinal(sunday);
      let candidate = nextSunday;
      while (getSundayOrdinal(candidate) !== ordinal) {
        candidate = addDayOffset(candidate, 7);
      }
      return candidate;
    }
    default:
      ensureUnreachable(kind);
  }
}

/**
 * Where a blank on `sunday` at a row of `kind` stands once the Sundays before `firstUnwritten` are written down (issue
 * #4192). Each passed Sunday's blank moves to the next Sunday in scope (getNextSundayInScope) when that holds nothing
 * at the row and is dropped otherwise, so Sunday by Sunday it walks the Sundays in scope: it stands on the first one on
 * or after `firstUnwritten` when that Sunday and every Sunday in scope before it hold nothing at the row (`entries`).
 * Undefined when one of them holds something, when the scope ends first (the end of the month for Same all month), and
 * for Does not repeat. The blank is taken to be on `sunday` whether or not `entries` holds it there. With
 * `firstUnwritten` the Sunday after `sunday`, it is that one move, which writing `sunday` down makes
 * (getPassedSundayWrites): the one rule for where a passed blank goes.
 */
function getBlankLanding(
  kind: RepeatKind,
  sunday: DateString,
  firstUnwritten: DateString,
  entries: RowEntries,
): DateString | undefined {
  for (let next = getNextSundayInScope(kind, sunday); next !== undefined; next = getNextSundayInScope(kind, next)) {
    if (entries.get(next) !== undefined) {
      return undefined;
    }
    if (next >= firstUnwritten) {
      return next;
    }
  }
  return undefined;
}

// `songs` with `entry` inserted before the first entry of a later row, so the stored order of the others is kept
function withEntryInserted(songs: SongListEntry[], entry: SongListEntry): SongListEntry[] {
  const index = songs.findIndex(({ row }) => row > entry.row);
  return index < 0 ? [...songs, entry] : [...songs.slice(0, index), entry, ...songs.slice(index)];
}

// SongListChanges whose lists are all worship services, which carry their date
export interface WorshipServiceChanges extends SongListChanges {
  update: EventSongList[];
  remove: EventSongList[];
}

// a Sunday worship service: the only list the passed-Sunday writes read or write (see the module comment)
export function isSundayWorshipService(songList: SongList): songList is EventSongList {
  return songList.type === SongListType.WorshipService && getDayOfWeek(songList.date) === DayOfWeek.Sunday;
}

// the Sunday worship service of `date` in `songLists`, the one RowEntries reads; undefined on a weekday
export function findWorshipService(songLists: SongList[], date: DateString): EventSongList | undefined {
  return songLists.find(
    (songList): songList is EventSongList => isSundayWorshipService(songList) && songList.date === date,
  );
}

// The lists a set of writes changes, by date: each a copy of the date's worship service in `songLists` (the one
// RowEntries reads), or a new one when the date has none
class WrittenLists {
  private readonly lists = new Map<DateString, EventSongList>();

  constructor(private readonly songLists: SongList[]) {}

  // the list of `date` as written so far, copied from `songLists` on first use
  listFor(date: DateString): EventSongList {
    let list = this.lists.get(date);
    if (!list) {
      const stored = findWorshipService(this.songLists, date);
      list = stored ? { ...stored, songs: [...stored.songs] } : { type: SongListType.WorshipService, date, songs: [] };
      this.lists.set(date, list);
    }
    return list;
  }

  // the blank of `rowIndex` removed from the list of `date`
  removeBlank(date: DateString, rowIndex: number): void {
    const list = this.listFor(date);
    list.songs = list.songs.filter((entry) => entry.row !== rowIndex || !isBlankEntry(entry));
  }

  // `entry` inserted at its row into the list of `date`
  insert(date: DateString, entry: SongListEntry): void {
    const list = this.listFor(date);
    list.songs = withEntryInserted(list.songs, entry);
  }

  // The blank of `rowIndex` on `date` moved to where it stands once the Sundays before `firstUnwritten` are written
  // down (getBlankLanding, for the row's kind in `schemaRows` and its entries in `songLists`), or dropped when it
  // stands nowhere
  moveBlank(
    date: DateString,
    rowIndex: number,
    firstUnwritten: DateString,
    schemaRows: SongListSchemaEntry[],
    songLists: SongList[],
  ): void {
    this.removeBlank(date, rowIndex);
    const landing = getBlankLanding(
      getRepeatKind(schemaRows.find(({ row }) => row === rowIndex)),
      date,
      firstUnwritten,
      getRowEntries(rowIndex, songLists),
    );
    if (landing) {
      this.insert(landing, blankEntry(rowIndex));
    }
  }

  // the lists written, `update` in ascending date order and `remove` holding those left with no entry, saved or not
  getChanges(): WorshipServiceChanges {
    const changes: WorshipServiceChanges = { update: [], remove: [] };
    for (const date of [...this.lists.keys()].sort()) {
      const list = this.lists.get(date)!;
      if (list.songs.length > 0) {
        changes.update.push(list);
      } else {
        changes.remove.push(list);
      }
    }
    return changes;
  }
}

// the rows at which `songList` holds a blank
function getBlankRows(songList: SongList | undefined): Set<number> {
  return new Set((songList?.songs ?? []).filter(isBlankEntry).map(({ row }) => row));
}

export interface PassedSundayWriteOptions {
  // write only the fill and leave the Sunday's blanks where they are: for the current Sunday, whose day has begun but
  // which has not passed, where a blank is legal (getWriteDownBeforeRowChange)
  keepBlanks?: boolean;
}

// The writes that record what a church's rows showed on `sunday`, a Sunday that has passed, so that its songs no
// longer depend on the repeat staying on (issue #4192):
// - a row that repeats and that the Sunday holds nothing at: the entry the row filled in there, as the Sunday showed
//   while it was current, is written into its weekly list at the row, verses included; nothing when the fill shows
//   nothing, and nothing on a Sunday before the row's repeatFrom (getRepeatFrom), which the row never filled;
// - a blank at any row, whatever its kind and whether or not the schema still has the row: the blank is removed,
//   since the past never holds a blank. At a row that repeats, when the next Sunday in the row's scope
//   (getNextSundayInScope) holds nothing at the row, a blank is written there (getBlankLanding one Sunday on; into a
//   new weekly list when that Sunday has none), so the block goes on interrupting the repeat; otherwise the blank is
//   simply dropped;
// - a song or a text: nothing.
// With `keepBlanks`, only the fill is written. The lists come back as SongListChanges: `update` in ascending date
// order, one list per date however many rows touch it, and `remove` holding the list of `sunday` when it is left with
// no entry at all (only a list with an id: one without is simply not written). Any other org yields no writes.
export function getPassedSundayWrites(
  org: Org,
  sunday: DateString,
  songLists: SongList[],
  options: PassedSundayWriteOptions = {},
): WorshipServiceChanges {
  return withSavedRemovesOnly(getUnsavedPassedSundayWrites(org, sunday, songLists, options));
}

// getPassedSundayWrites, with `remove` also holding a list without an id that is left with no entry, so that a chain
// of writes (getChainedWrites) drops it from the lists the next write reads
function getUnsavedPassedSundayWrites(
  org: Org,
  sunday: DateString,
  songLists: SongList[],
  { keepBlanks = false }: PassedSundayWriteOptions,
): WorshipServiceChanges {
  if (org.type !== OrgType.Church) {
    return { update: [], remove: [] };
  }

  const written = new WrittenLists(songLists);
  const schemaRows = org.churchLiturgySchema?.rows ?? [];
  for (const row of schemaRows) {
    const kind = getRepeatKind(row);
    if (kind === RepeatKind.DoesNotRepeat) {
      continue;
    }
    const entries = getRowEntries(row.row, songLists);
    if (entries.get(sunday) !== undefined) {
      continue;
    }
    const { entry } = resolveRepeatingCell({
      kind,
      date: sunday,
      currentSunday: sunday,
      repeatFrom: getRepeatFrom(row),
      entries,
    });
    if (entry) {
      written.insert(sunday, { ...entry, row: row.row });
    }
  }

  if (!keepBlanks) {
    for (const rowIndex of getBlankRows(findWorshipService(songLists, sunday))) {
      written.moveBlank(sunday, rowIndex, addDayOffset(sunday, 7), schemaRows, songLists);
    }
  }
  return written.getChanges();
}

// `changes` without the removed lists that were never saved, which there is nothing to remove of
function withSavedRemovesOnly({ update, remove }: WorshipServiceChanges): WorshipServiceChanges {
  return { update, remove: remove.filter(({ id }) => id !== undefined) };
}

// the first and last of the Sundays the housekeeping server writes down for one church (getStaleBlankWrites)
export interface WrittenSundayRange {
  first: DateString;
  last: DateString;
}

/**
 * The stale-blank writes (issue #4192), made after the passed Sundays from `first` through `last` are written down
 * (getPassedSundayRangeWrites, run on the lists with the stale blanks still in place, so every fill sees them;
 * `songLists` hold its writes). A stale blank is one on a church's Sunday worship service dated before `first`, a
 * Sunday the write-down never visits: one left by #4191, or by a first run in an environment. It goes, since the past
 * never holds a blank, and what every Sunday from `first` on shows is preserved:
 * - at a row that repeats, the blank is written where it stands once the Sundays through `last` are written down
 *   (getBlankLanding): the first Sunday in the row's scope after `last`, when no Sunday in scope up to it holds
 *   something at the row, so the block goes on interrupting the repeat. A Sunday that holds something ends the block,
 *   and so does the end of the scope (the end of the month for Same all month): the blank is then dropped. The Sundays
 *   through `last` the walk passes hold nothing at the row, so they showed nothing and were written down as nothing,
 *   as the blank intended;
 * - at a row that does not repeat, or one the schema lacks: the blank is dropped.
 * The list a stale blank leaves empty is removed (one with an id: one without is simply not written). `update` comes in
 * ascending date order. Any other org yields no writes.
 */
export function getStaleBlankWrites(
  org: Org,
  { first, last }: WrittenSundayRange,
  songLists: SongList[],
): WorshipServiceChanges {
  if (org.type !== OrgType.Church) {
    return { update: [], remove: [] };
  }
  const blanked = songLists
    .filter(
      (songList): songList is EventSongList =>
        isSundayWorshipService(songList) && songList.date < first && songList.songs.some(isBlankEntry),
    )
    .sort((lhs, rhs) => lhs.date.localeCompare(rhs.date));
  const written = new WrittenLists(songLists);
  const schemaRows = org.churchLiturgySchema?.rows ?? [];
  for (const list of blanked) {
    for (const rowIndex of getBlankRows(list)) {
      // the walk reads `songLists`, where the stale blanks are all still in place: a later one at the row ends the block
      written.moveBlank(list.date, rowIndex, addDayOffset(last, 7), schemaRows, songLists);
    }
  }
  return withSavedRemovesOnly(written.getChanges());
}

/**
 * The writes of all the passed Sundays in `sundays` (issue #4192): each Sunday is written down in ascending date order
 * with getPassedSundayWrites, computed on the lists as the earlier Sundays' writes left them, so a Sunday's fill reads
 * the entry just written on the Sunday before and a blank moves on Sunday by Sunday. The writes come back as one
 * SongListChanges (getChainedWrites). Any other org yields no writes.
 */
export function getPassedSundayRangeWrites(
  org: Org,
  sundays: DateString[],
  songLists: SongList[],
): WorshipServiceChanges {
  return getChainedWrites(
    songLists,
    [...sundays].sort().map((sunday) => (lists) => getUnsavedPassedSundayWrites(org, sunday, lists, {})),
  );
}

// The Sundays from `first` through `last`, in ascending order; none when `first` is later. Both must be Sundays.
// getAllEnclosingSundays starts at the last Sunday before its `min` (lastSundayDate, the Sunday a week earlier when
// `min` is a Sunday), so it is asked from the Monday after `first`, whose last Sunday is `first`; asked from `first`
// itself it would start a week early.
export function sundaysFrom(first: DateString, last: DateString): DateString[] {
  return getAllEnclosingSundays(addDayOffset(first, 1), last);
}

// `songLists` with `changes` applied: each updated list in place of the worship service of its date (or added when
// the date has none), each removed list dropped
function withChangesApplied(songLists: SongList[], { update, remove }: WorshipServiceChanges): SongList[] {
  const replaced = new Map<SongList, SongList | undefined>();
  const added: SongList[] = [];
  for (const list of update) {
    const stored = findWorshipService(songLists, list.date);
    if (stored) {
      replaced.set(stored, list);
    } else {
      added.push(list);
    }
  }
  for (const list of remove) {
    const stored = findWorshipService(songLists, list.date);
    if (stored) {
      replaced.set(stored, undefined);
    }
  }
  const kept = songLists.flatMap((songList) => (replaced.has(songList) ? (replaced.get(songList) ?? []) : [songList]));
  return [...kept, ...added];
}

/**
 * The writes the planner makes before a change to a church row that could alter what a Sunday shows: switching a
 * row's repeat kind (on, off or between kinds), Delete Row and Insert Row (issue #4209: the shift rewrites every list
 * holding a row below the new one, and a passed Sunday's list rewritten drops a blank not yet moved off it). Issue
 * #4192: "Turning a repeat off, switching its kind, and Delete Row write down any passed Sunday not yet written before
 * they change anything, so the outcome never depends on timing." It writes down, under the schema as it stands before
 * the change, the Sundays whose day has begun for the admin on `today` and that the server has not written yet, which
 * are the Sundays the planner goes on showing filled (getFillStartSunday):
 * - the passed Sundays, from the first Sunday the fill applies to up to the one before the current Sunday
 *   (thisSundayDate(today), getUnwrittenPassedSundays), in full and in order (getPassedSundayRangeWrites): their fill
 *   written and their blanks removed or moved on. With `writtenThrough`, the last Sunday the server has written the
 *   church down through (GET /api/passed-sundays), that is every Sunday after it, however long the server has been
 *   behind. A Sunday at or before it is never written again: the admin may since have cleared a cell there (a past
 *   clear removes the entry), which writing it again would refill. Without `writtenThrough` (a planner that has not
 *   heard from the server), the fallback window: the Sunday before the current one while `now` is before its Monday
 *   at passedSundayShownFilledUntilHourUtc UTC, and nothing after. The server writes from passedSundayWriteHourUtc
 *   (10:00 UTC), so in the fallback, between 10:00 and 12:00, an admin who clears the written cell and then switches a
 *   kind gets it written again; that is accepted for a two-hour overnight window;
 * - today, when today is a Sunday (the current Sunday): its fill only. Michael's Verify for the issue: "Turning the
 *   repeat off on a Sunday evening, before the automatic write, still leaves that Sunday holding A." Its blanks stay
 *   where they are: a blank on the current Sunday is legal, and moving it on would leave the Sunday holding nothing,
 *   which the server's later write would then fill under the new kind. The server removes it once the Sunday passes.
 *   A clear there writes a blank, which the fill-only write respects.
 * The passed Sundays are written first and today's is computed on the result; all come back as one SongListChanges
 * (`changes`), a list touched by several once with its final content, `update` in ascending date order and `remove`
 * holding the removed lists that have an id, with `writtenDown` the first and last passed Sundays written down. A
 * Sunday already written down, and a Sunday with no repeating row and no blank, yield nothing, so the planner can call
 * this before every such change. Any other org yields no writes.
 */
export function getWriteDownBeforeRowChange(
  org: Org,
  songLists: SongList[],
  { today, now, writtenThrough }: { today: DateString; now: Date; writtenThrough?: DateString },
): RowChangeWriteDown {
  const currentSunday = thisSundayDate(today);
  const passedSundays = getUnwrittenPassedSundays(
    getFillStartSunday(now, currentSunday, writtenThrough),
    currentSunday,
  );
  const writes: SongListWrites[] = [(lists) => getPassedSundayRangeWrites(org, passedSundays, lists)];
  if (getDayOfWeek(today) === DayOfWeek.Sunday) {
    writes.push((lists) => getUnsavedPassedSundayWrites(org, today, lists, { keepBlanks: true }));
  }
  const writtenDown = getPassedSundaysWrittenDown(passedSundays);
  return {
    changes: getChainedWrites(songLists, writes),
    ...(org.type === OrgType.Church && writtenDown && { writtenDown }),
  };
}

// what getWriteDownBeforeRowChange writes
export interface RowChangeWriteDown {
  changes: WorshipServiceChanges;
  // A church's passed Sundays written down, the first and last of getUnwrittenPassedSundays, even when `changes`
  // change no list there: what the planner has then written the church down (issue #4209), which it tells the server
  // (writePassedSundaysDown in client/src/data/passed_sundays_written_through.ts) so the housekeeping write-down leaves
  // those Sundays as the planner wrote them. Absent when every passed Sunday is written down, and for any other org.
  writtenDown?: PassedSundaysWrittenDownMark;
}

// the first and last of `sundays`, the passed Sundays a write-down covers in ascending order: the mark the planner
// tells the server (issue #4209), which it stores only when `from` leaves no Sunday unwritten before it; undefined
// when none
export function getPassedSundaysWrittenDown(sundays: DateString[]): PassedSundaysWrittenDownMark | undefined {
  const [from] = sundays;
  const writtenThrough = sundays.at(-1);
  return from !== undefined && writtenThrough !== undefined ? { from, writtenThrough } : undefined;
}

/**
 * The passed Sundays a planner writes down before an edit of a church's lists (getWriteDownBeforeRowChange, and
 * planner_edits.ts for the grid's edits, issue #4209), in ascending order: from `fillStartSunday`, the first Sunday the
 * fill applies to on screen (getFillStartSunday), up to the one before `currentSunday`; these are the passed Sundays it
 * goes on showing filled, the ones not yet written down. Empty when every passed Sunday is written down.
 */
export function getUnwrittenPassedSundays(fillStartSunday: DateString, currentSunday: DateString): DateString[] {
  return sundaysFrom(fillStartSunday, addDayOffset(currentSunday, -7));
}

/**
 * `list` with the blanks the housekeeping server moves onto it carried forward, for a write by a client whose cache
 * may not have heard of the move yet (issue #4192). The server moves a passed Sunday's blank on to the next Sunday in
 * the row's scope (getPassedSundayWrites), Sunday after Sunday while each holds nothing, and tells only its own
 * sockets, so a planner elsewhere may still cache an earlier passed Sunday with the blank and the Sundays after it
 * without it, however many weeks stale; uploading the Sunday the blank now stands on whole would drop the blank and
 * start the repeat filling there again. For a church's Sunday worship service dated on or after `currentSunday`, at
 * each repeating row of the schema `list` holds nothing at: when the row's fill for the list's date (read from
 * `songLists`, the cache, with `list` in place of its date's list) resolves to a blank on a Sunday before
 * `currentSunday`, and the list's date is where that blank stands once the passed Sundays are written down
 * (getBlankLanding from `currentSunday`: the first Sunday in scope on or after it, the Sundays in scope between
 * holding nothing in the cache, which is why the fill reaches the blank), the list gets a blank at the row, which is
 * where the server has put it or will. A blank there shows what the fill showed, nothing, so the list shows the same;
 * a second call changes nothing. Comes back as `list` itself when nothing is carried, and for a school, a small group
 * or any other list.
 */
export function withCarriedPassedBlanks(
  org: Pick<Org, 'type' | 'churchLiturgySchema'> | undefined,
  list: SongList,
  songLists: SongList[],
  currentSunday: DateString,
): SongList {
  if (org?.type !== OrgType.Church || !isSundayWorshipService(list) || list.date < currentSunday) {
    return list;
  }
  const { date } = list;
  const lists = [
    list,
    ...songLists.filter((songList) => songList.type !== SongListType.WorshipService || songList.date !== date),
  ];
  let songs = list.songs;
  for (const row of org.churchLiturgySchema?.rows ?? []) {
    const kind = getRepeatKind(row);
    if (kind === RepeatKind.DoesNotRepeat || list.songs.some((entry) => entry.row === row.row)) {
      continue;
    }
    const entries = getRowEntries(row.row, lists);
    const { sourceDate } = resolveRepeatingCell({
      kind,
      date,
      currentSunday,
      repeatFrom: getRepeatFrom(row),
      entries,
    });
    if (
      sourceDate !== undefined &&
      sourceDate < currentSunday &&
      isBlankEntry(entries.get(sourceDate)) &&
      getBlankLanding(kind, sourceDate, currentSunday, entries) === date
    ) {
      songs = withEntryInserted(songs, blankEntry(row.row));
    }
  }
  return songs === list.songs ? list : { ...list, songs };
}

// one step of a chain of writes: the changes to make to `songLists`, which hold the earlier steps' changes
export type SongListWrites = (songLists: SongList[]) => WorshipServiceChanges;

/**
 * The changes of `writes` run in order, each computed on `songLists` as the earlier ones left them (withChangesApplied),
 * as one SongListChanges: a list touched by several appears once with its final content, `update` and `remove` in
 * ascending date order, and `remove` holding only the lists with an id (a list created by one step and emptied by a
 * later one is not written at all). A step may report a removed list without an id, so that later steps no longer see
 * it. A saved list one step removes and a later step writes the date of again, as a new list, is written under the
 * saved list's id: it is that Sunday's worship service, and a second list of the same Sunday would otherwise sit beside
 * the saved one until the server's answer.
 */
export function getChainedWrites(songLists: SongList[], writes: SongListWrites[]): WorshipServiceChanges {
  // the final write of each date: its list, or undefined when the list is removed
  const final = new Map<DateString, EventSongList | undefined>();
  // the saved list removed at each date, which must still be removed when the date ends with no list
  const removedSaved = new Map<DateString, EventSongList>();
  let lists = songLists;
  for (const write of writes) {
    const changes = write(lists);
    lists = withChangesApplied(lists, changes);
    for (const list of changes.remove) {
      final.set(list.date, undefined);
      if (list.id !== undefined) {
        removedSaved.set(list.date, list);
      }
    }
    changes.update.forEach((list) => final.set(list.date, list));
  }
  const changes: WorshipServiceChanges = { update: [], remove: [] };
  for (const date of [...final.keys()].sort()) {
    const list = final.get(date);
    const removed = removedSaved.get(date);
    if (list) {
      changes.update.push(list.id === undefined && removed ? { ...list, id: removed.id } : list);
    } else if (removed) {
      changes.remove.push(removed);
    }
  }
  return changes;
}
